import { useMutation } from "@tanstack/react-query";
import {
  upsertRecipe,
  deleteRecipe,
  deleteRecipeFamily,
  setRecipeFamilyEnable,
  upsertRecipeLayer,
  deleteRecipeLayer,
  reorderRecipeLayer,
  setRecipeAsCurrent,
  upgradeRecipe,
  cloneRecipe,
} from "@/pages/sy/repositories/RecipeRepository";
import type { Recipe } from "@/pages/sy/models/Recipe";
import type { RecipeLayer } from "@/pages/sy/models/RecipeLayer";

// =============================================================================
// Recipe Mutations
// =============================================================================
// NOTE: These mutations do NOT invalidate queries on success.
// Cache invalidation is handled by SignalR (useRealtimeInvalidation)
// which provides immediate cross-client sync without duplicate fetches.

export const useUpsertRecipeMutation = () => {
  return useMutation({
    mutationFn: async (recipe: Recipe) => {
      const resp = await upsertRecipe(recipe);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};

export const useDeleteRecipeMutation = () => {
  return useMutation({
    mutationFn: async (recipeId: number) => {
      const resp = await deleteRecipe(recipeId);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};

export const useDeleteRecipeFamilyMutation = () => {
  return useMutation({
    mutationFn: async (originalRecipeId: number) => {
      const resp = await deleteRecipeFamily(originalRecipeId);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};

export const useSetRecipeFamilyEnableMutation = () => {
  return useMutation({
    mutationFn: async ({
      originalRecipeId,
      isEnable,
    }: {
      originalRecipeId: number;
      isEnable: boolean;
    }) => {
      const resp = await setRecipeFamilyEnable(originalRecipeId, isEnable);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};

export const useSetRecipeAsCurrentMutation = () => {
  return useMutation({
    mutationFn: async (recipeId: number) => {
      const resp = await setRecipeAsCurrent(recipeId);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};

export const useUpgradeRecipeMutation = () => {
  return useMutation({
    mutationFn: async (recipeId: number) => {
      const resp = await upgradeRecipe(recipeId);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};

export const useCloneRecipeMutation = () => {
  return useMutation({
    mutationFn: async ({
      recipeId,
      recipeName,
    }: {
      recipeId: number;
      recipeName?: string;
    }) => {
      const resp = await cloneRecipe(recipeId, recipeName);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};

// =============================================================================
// Recipe Layer Mutations
// =============================================================================

export const useUpsertRecipeLayerMutation = () => {
  return useMutation({
    mutationFn: async (recipeLayer: Partial<RecipeLayer>) => {
      const resp = await upsertRecipeLayer(recipeLayer as RecipeLayer);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};

export const useDeleteRecipeLayerMutation = () => {
  return useMutation({
    mutationFn: async (recipeLayerId: number) => {
      const resp = await deleteRecipeLayer(recipeLayerId);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};

export const useReorderRecipeLayerMutation = () => {
  return useMutation({
    mutationFn: async ({
      recipeLayerId,
      newLayerNumber,
    }: {
      recipeLayerId: number;
      newLayerNumber: number;
    }) => {
      const resp = await reorderRecipeLayer(recipeLayerId, newLayerNumber);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};
